import { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getIsSummaryPanelOpen } from 'store/dealManagement/selectors/getIsSummaryPanelOpen';
import {
  FailedDealLine,
  transformLinesAllocationFailuresToToasts,
} from 'components/pages/Planner/hooks/utils/transformLinesAllocationFailuresToToasts';
import {
  DirectSalesCampaignDefinitionResponse,
  DirectSalesAllocationSolutionResponse,
  DirectSalesPricingSolutionResponse,
  DirectSalesStatusOption,
  DirectSalesFailedStatus,
} from 'components/common/types/DirectSalesCampaign.types';
import { addToastNotification, addNotifications } from 'store/notification/reducer';

export type PollCompleteHandlerResult = {
  campaignDefinition: DirectSalesCampaignDefinitionResponse;
  allocationSolution: DirectSalesAllocationSolutionResponse | null;
  pricingSolution: DirectSalesPricingSolutionResponse | null;
};

export type AllocationFailureOption = {
  status: DirectSalesFailedStatus;
};

type UsePollCompleteErrorHandler = {
  handleDraftFail: (option?: AllocationFailureOption) => Promise<void>;
  handleDraftFailedLines: (failedLines: FailedDealLine[], options?: AllocationFailureOption) => void;
  handlePollCompleteDraftFail: (
    pollCompleteHandler: Promise<PollCompleteHandlerResult>,
    option?: AllocationFailureOption,
  ) => Promise<void>;
};

export const useDirectSalesDraftErrorHandler = (): UsePollCompleteErrorHandler => {
  const dispatch = useDispatch();
  const isSummaryPanelOpen = useSelector(getIsSummaryPanelOpen);
  const isSummaryPanelOpenRef = useRef(isSummaryPanelOpen);
  isSummaryPanelOpenRef.current = isSummaryPanelOpen;

  const handleDraftFail = async (option?: AllocationFailureOption): Promise<void> => {
    if (isSummaryPanelOpenRef.current) return;

    dispatch(
      addToastNotification({
        header:
          option?.status === DirectSalesStatusOption.DRAFT_FAILED_PRICE
            ? 'Campaign pricing failed'
            : 'Campaign allocation failed',
        description: 'Please reallocate your campaign',
        timeout: 10000,
        type: 'Failure',
      }),
    );
  };

  const handleDraftFailedLines = (failedLines: FailedDealLine[], options?: AllocationFailureOption): void => {
    if (isSummaryPanelOpenRef.current) return;

    const toastNotifications = transformLinesAllocationFailuresToToasts(failedLines ?? []);

    if (toastNotifications.length) {
      dispatch(addNotifications(toastNotifications));
    } else {
      handleDraftFail(options);
    }
  };

  const handlePollCompleteDraftFail = async (
    pollCompleteHandler?: Promise<PollCompleteHandlerResult>,
    option?: AllocationFailureOption,
  ): Promise<void> => {
    const campaignDefinition = (await pollCompleteHandler)?.campaignDefinition ?? { dealLines: [] };

    handleDraftFailedLines(campaignDefinition.dealLines ?? [], option);
  };

  return { handleDraftFail, handlePollCompleteDraftFail, handleDraftFailedLines };
};
